import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react'

import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'
import { UiCard } from '@/ui/UiCard'

import IntersectionAnchor from './IntersectionAnchor'
import QuestionMark from './QuestionMark'

export const InfiniteListLoadingStates = {
  Initial: 'initial',
  Loading: 'loading',
  NextLoading: 'next-loading',
  NextError: 'next-error',
  Error: 'error',
  Idle: 'idle',
} as const

export type InfiniteListLoadingState =
  (typeof InfiniteListLoadingStates)[keyof typeof InfiniteListLoadingStates]

interface Props<D> extends PropsWithChildren {
  items: D[]
  loadingState: InfiniteListLoadingState
  hasNextPage?: boolean
  loadNextThrottleMs?: number
  errorTitle?: string
  noDataTitle?: string
  noDataDescription?: string
  noDataAction?: ReactNode
  onRetry: () => void
  onLoadNext: () => void
}

export default function InfiniteList<D>({
  items,
  loadingState,
  hasNextPage = true,
  loadNextThrottleMs = 800,
  errorTitle = 'Failed to load list',
  noDataTitle = 'No items yet',
  noDataDescription,
  noDataAction,
  children,
  onRetry,
  onLoadNext,
}: Props<D>) {
  const lastLoadNextAtMsRef = useRef<number>(0)
  const loadNextTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const isLoadNextQueuedRef = useRef(false)

  const hasNextPageRef = useRef(hasNextPage)
  const onLoadNextRef = useRef(onLoadNext)
  const loadNextThrottleMsRef = useRef(loadNextThrottleMs)

  hasNextPageRef.current = hasNextPage
  onLoadNextRef.current = onLoadNext
  loadNextThrottleMsRef.current = loadNextThrottleMs

  useEffect(() => {
    return () => {
      if (loadNextTimeoutIdRef.current) {
        globalThis.clearTimeout(loadNextTimeoutIdRef.current)
        loadNextTimeoutIdRef.current = null
      }
    }
  }, [])

  const requestNextPage = useCallback(() => {
    if (!hasNextPageRef.current) return

    // Prevent multiple requests while already loading
    if (loadingState === InfiniteListLoadingStates.NextLoading) return

    const nowMs = Date.now()
    const throttleMs = Math.max(0, loadNextThrottleMsRef.current)
    const elapsedMs = nowMs - lastLoadNextAtMsRef.current

    if (elapsedMs >= throttleMs) {
      lastLoadNextAtMsRef.current = nowMs
      onLoadNextRef.current()
      return
    }

    isLoadNextQueuedRef.current = true

    if (loadNextTimeoutIdRef.current) return

    const remainingMs = Math.max(0, throttleMs - elapsedMs)
    loadNextTimeoutIdRef.current = globalThis.setTimeout(() => {
      loadNextTimeoutIdRef.current = null

      if (!isLoadNextQueuedRef.current) return
      isLoadNextQueuedRef.current = false

      if (!hasNextPageRef.current) return

      lastLoadNextAtMsRef.current = Date.now()
      onLoadNextRef.current()
    }, remainingMs)
  }, [loadingState])

  if (items.length > 0) {
    return (
      <div className='relative space-y-4'>
        {children}
        {(() => {
          if (!hasNextPage) return null

          if (loadingState === InfiniteListLoadingStates.NextLoading) {
            return (
              <div className='flex justify-center py-2'>
                <UiRing size={48} color='white' />
              </div>
            )
          }

          if (loadingState === InfiniteListLoadingStates.NextError) {
            return (
              <div className='flex justify-center py-2'>
                <UiButton variant='outline' size='sm' onClick={requestNextPage}>
                  Load more
                </UiButton>
              </div>
            )
          }

          return (
            <IntersectionAnchor
              bottomPx={600}
              throttleMs={0}
              onIntersect={requestNextPage}
            />
          )
        })()}
      </div>
    )
  }

  if (
    loadingState === InfiniteListLoadingStates.Loading ||
    loadingState === InfiniteListLoadingStates.Initial
  ) {
    return (
      <div className='flex justify-center py-12'>
        <UiRing size={48} color='white' />
      </div>
    )
  }

  if (loadingState === InfiniteListLoadingStates.Error) {
    return (
      <UiCard className='bg-card text-foreground rounded-lg p-6 text-center'>
        <p className='text-2xl font-semibold text-red-400'>{errorTitle}</p>
        <div className='mt-4 flex justify-center'>
          <UiButton variant='outline' size='sm' onClick={onRetry}>
            Retry
          </UiButton>
        </div>
      </UiCard>
    )
  }

  return (
    <div className='text-foreground flex w-full flex-col items-center gap-2.5 rounded-lg p-6 text-center'>
      <div className='text-primary bg-primary-foreground aspect-square w-25 rounded-xl p-6'>
        <QuestionMark />
      </div>
      <p className='mt-5 text-xl'>{noDataTitle}</p>
      {noDataDescription && (
        <p className='text-muted-foreground'>{noDataDescription}</p>
      )}
      {noDataAction && (
        <div className='mt-4 flex justify-center'>{noDataAction}</div>
      )}
    </div>
  )
}
